import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  FileText, Plus, Search, Building2, Calendar, Euro,
  ChevronRight, Filter, Shield, AlertCircle, CheckCircle,
  XCircle, Loader2, Edit, Trash2, Eye, History, Upload
} from 'lucide-react'
import { useAuth } from '../../../contexts/AuthContext'
import { toast } from 'sonner'
import ImportModal from '../../../components/shared/ImportModal'
import PoliceFranchisesPanel from './PoliceFranchisesPanel'

const API_URL = process.env.REACT_APP_BACKEND_URL

const TYPE_POLICE_CONFIG = {
  dab: { label: 'Dommages aux biens', color: 'bg-blue-100 text-blue-700' },
  flotte: { label: 'Flotte automobile', color: 'bg-purple-100 text-purple-700' },
  rc_pro: { label: 'RC Professionnelle', color: 'bg-amber-100 text-amber-700' },
  multirisque: { label: 'Multirisque', color: 'bg-teal-100 text-teal-700' },
  cyber: { label: 'Cyber-risques', color: 'bg-pink-100 text-pink-700' },
  rc_transport: { label: 'RC Transport', color: 'bg-sky-100 text-sky-700' },
  ad_valorem: { label: 'Ad valorem', color: 'bg-cyan-100 text-cyan-700' },
  autre: { label: 'Autre', color: 'bg-slate-100 text-slate-700' }
}

const STATUT_CONFIG = {
  en_cours: { label: 'En cours', color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  suspendue: { label: 'Suspendue', color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  resiliee: { label: 'Résiliée', color: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
  en_attente: { label: 'En attente', color: 'bg-slate-100 text-slate-600', dot: 'bg-slate-400' }
}

function formatDate(dateStr) {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  })
}

function formatMontant(montant) {
  if (!montant) return '—'
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(montant)
}

export default function C2Polices({ forceTenantId = null }) {
  const navigate = useNavigate()
  const { profile } = useAuth()
  const [polices, setPolices] = useState([])
  const [compagnies, setCompagnies] = useState([])
  const [tenants, setTenants] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatut, setFilterStatut] = useState('')
  const [filterType, setFilterType] = useState('')
  const [filterTenant, setFilterTenant] = useState(forceTenantId || '')
  
  // Modal
  const [showModal, setShowModal] = useState(false)
  const [editingPolice, setEditingPolice] = useState(null)
  const [saving, setSaving] = useState(false)
  
  // Prime History panel
  const [showPrimeHistory, setShowPrimeHistory] = useState(null)
  const [showPrimeImport, setShowPrimeImport] = useState(false)
  const [showFranchises, setShowFranchises] = useState(null)

  // Fetch polices
  const fetchPolices = useCallback(async () => {
    try {
      let url = `${API_URL}/api/polices`
      const params = new URLSearchParams()
      if (filterStatut) params.append('statut', filterStatut)
      if (params.toString()) url += `?${params.toString()}`
      
      const response = await fetch(url)
      if (response.ok) {
        const data = await response.json()
        setPolices(data.polices || [])
        
        // Extract unique tenants from polices
        const uniqueTenants = [...new Map(
          (data.polices || [])
            .filter(p => p.tenants)
            .map(p => [p.tenants.id, p.tenants])
        ).values()]
        
        // If forceTenantId, ensure it's in the tenants list
        if (forceTenantId && !uniqueTenants.find(t => t.id === forceTenantId)) {
          try {
            const tRes = await fetch(`${API_URL}/api/admin/tenants`)
            const tData = await tRes.json()
            const allTenants = tData.tenants || []
            const current = allTenants.find(t => t.id === forceTenantId)
            if (current) uniqueTenants.push({ id: current.id, nom: current.nom })
          } catch {}
        }
        setTenants(uniqueTenants)
      }
    } catch (err) {
      setError('Erreur lors du chargement des polices')
    } finally {
      setLoading(false)
    }
  }, [filterStatut, forceTenantId])

  // Fetch compagnies
  const fetchCompagnies = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/compagnies`)
      if (response.ok) {
        const data = await response.json()
        setCompagnies(data.compagnies || [])
      }
    } catch (err) {
      console.error('Erreur chargement compagnies:', err)
    }
  }, [])

  useEffect(() => {
    fetchPolices()
    fetchCompagnies()
  }, [fetchPolices, fetchCompagnies])

  // Filter polices
  const filteredPolices = polices.filter(p => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase()
      if (!p.numero_police?.toLowerCase().includes(search) &&
          !p.libelle?.toLowerCase().includes(search) &&
          !p.tenants?.nom?.toLowerCase().includes(search) &&
          !p.compagnies?.nom?.toLowerCase().includes(search)) {
        return false
      }
    }
    if (filterType && p.type_police !== filterType) return false
    if (filterTenant && p.tenant_id !== filterTenant) return false
    return true
  })

  // Stats
  const stats = {
    total: polices.length,
    enCours: polices.filter(p => p.statut === 'en_cours').length,
    expireBientot: polices.filter(p => {
      if (!p.date_echeance) return false
      const echeance = new Date(p.date_echeance)
      const dans30j = new Date()
      dans30j.setDate(dans30j.getDate() + 30)
      return echeance <= dans30j && p.statut === 'en_cours'
    }).length
  }

  // Handle save
  const handleSave = async (formData) => {
    setSaving(true)
    try {
      const url = editingPolice 
        ? `${API_URL}/api/polices/${editingPolice.id}`
        : `${API_URL}/api/polices`
      
      const response = await fetch(url, {
        method: editingPolice ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      })
      
      if (response.ok) {
        toast.success(editingPolice ? 'Police mise à jour' : 'Police créée')
        setShowModal(false)
        setEditingPolice(null)
        fetchPolices()
      } else {
        const err = await response.json()
        toast.error(err.detail || 'Erreur lors de l\'enregistrement')
      }
    } catch (err) {
      toast.error('Erreur lors de l\'enregistrement')
    } finally {
      setSaving(false)
    }
  }

  // Handle delete
  const handleDelete = async (police) => {
    if (!window.confirm(`Résilier la police ${police.numero_police} ?`)) return
    
    try {
      const response = await fetch(`${API_URL}/api/polices/${police.id}`, {
        method: 'DELETE'
      })
      
      if (response.ok) {
        toast.success('Police résiliée')
        fetchPolices()
      } else {
        const err = await response.json()
        toast.error(err.detail || 'Erreur lors de la résiliation')
      }
    } catch (err) {
      toast.error('Erreur lors de la résiliation')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-6" data-testid="c2-polices-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Polices d'assurance</h1>
          <p className="text-sm text-slate-500">Gestion des contrats d'assurance clients</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowPrimeImport(true)}
            className="flex items-center gap-2 px-4 py-2.5 border border-slate-200 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors"
            data-testid="import-primes-btn"
          >
            <Upload className="w-4 h-4" />
            Import primes
          </button>
          <button
            onClick={() => { setEditingPolice(null); setShowModal(true) }}
            className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
            data-testid="create-police-btn"
          >
            <Plus className="w-4 h-4" />
            Nouvelle police
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
              <FileText className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
              <p className="text-xs text-slate-500">Total polices</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.enCours}</p>
              <p className="text-xs text-slate-500">En cours</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.expireBientot}</p>
              <p className="text-xs text-slate-500">Expire &lt; 30j</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
          
          <select
            value={filterStatut}
            onChange={e => setFilterStatut(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
          >
            <option value="">Tous statuts</option>
            {Object.entries(STATUT_CONFIG).map(([key, config]) => (
              <option key={key} value={key}>{config.label}</option>
            ))}
          </select>
          
          <select
            value={filterType}
            onChange={e => setFilterType(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
          >
            <option value="">Tous types</option>
            {Object.entries(TYPE_POLICE_CONFIG).map(([key, config]) => (
              <option key={key} value={key}>{config.label}</option>
            ))}
          </select>
          
          <select
            value={filterTenant}
            onChange={e => setFilterTenant(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
          >
            <option value="">Tous clients</option>
            {tenants.map(t => (
              <option key={t.id} value={t.id}>{t.nom}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Polices list */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {error && (
          <div className="p-4 bg-red-50 border-b border-red-100 text-red-700 text-sm">
            {error}
          </div>
        )}
        
        {filteredPolices.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">Aucune police trouvée</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Police</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Client</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Compagnie</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Échéance</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Franchise</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Statut</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredPolices.map(police => {
                const typeConfig = TYPE_POLICE_CONFIG[police.type_police] || TYPE_POLICE_CONFIG.autre
                const statutConfig = STATUT_CONFIG[police.statut] || STATUT_CONFIG.en_attente
                
                return (
                  <tr key={police.id} className="hover:bg-slate-50" data-testid={`police-${police.id}`}>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                          <Shield className="w-5 h-5 text-slate-500" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">{police.numero_police}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className={`px-2 py-0.5 text-xs font-medium rounded ${typeConfig.color}`}>
                              {typeConfig.label}
                            </span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-sm text-slate-700">{police.tenants?.nom || '—'}</p>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-sm text-slate-700">{police.compagnies?.nom || police.conditions_particulieres?.assureur || '—'}</p>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-sm text-slate-700">{formatDate(police.date_echeance)}</p>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-sm font-mono text-slate-700">{formatMontant(police.franchise_standard)}</p>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-full ${statutConfig.color}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${statutConfig.dot}`}></span>
                        {statutConfig.label}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setShowFranchises(police)}
                          className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Franchises"
                          data-testid={`police-franchises-btn-${police.id}`}
                        >
                          <Euro className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setShowPrimeHistory(police)}
                          className="p-2 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                          title="Historique primes"
                          data-testid={`police-primes-btn-${police.id}`}
                        >
                          <History className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => navigate(`/c2/polices/${police.id}`)}
                          className="p-2 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                          title="Voir détails"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => { setEditingPolice(police); setShowModal(true) }}
                          className="p-2 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                          title="Modifier"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        {police.statut === 'en_cours' && (
                          <button
                            onClick={() => handleDelete(police)}
                            className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Résilier"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <PoliceModal
          police={editingPolice}
          compagnies={compagnies}
          tenants={tenants}
          forceTenantId={forceTenantId}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditingPolice(null) }}
          saving={saving}
        />
      )}

      {/* Prime History Panel */}
      {showPrimeHistory && (
        <PrimeHistoryPanel
          police={showPrimeHistory}
          onClose={() => setShowPrimeHistory(null)}
        />
      )}

      {/* Prime Import Modal */}
      {showPrimeImport && (
        <ImportModal
          entityType="primes"
          tenants={tenants}
          onClose={() => setShowPrimeImport(false)}
          onDone={() => { setShowPrimeImport(false); fetchPolices() }}
        />
      )}

      {/* Franchises Panel */}
      {showFranchises && (
        <PoliceFranchisesPanel
          police={showFranchises}
          onClose={() => setShowFranchises(null)}
        />
      )}
    </div>
  )
}

// Modal component for create/edit (exporte : reutilise par la fiche client)
export function PoliceModal({ police, compagnies, tenants, forceTenantId, onSave, onClose, saving }) {
  const lockedTenantId = forceTenantId || police?.tenant_id || ''
  const isClientLocked = !!forceTenantId || !!police

  const [formData, setFormData] = useState({
    tenant_id: lockedTenantId,
    compagnie_id: police?.compagnie_id || '',
    numero_police: police?.numero_police || '',
    type_police: police?.type_police || 'dab',
    libelle: police?.libelle || '',
    date_effet: police?.date_effet || new Date().toISOString().split('T')[0],
    date_echeance: police?.date_echeance || '',
    franchise_standard: police?.franchise_standard || '',
    plafond_garantie: police?.plafond_garantie || '',
    gestionnaire_compagnie: police?.gestionnaire_compagnie || '',
    notes: police?.notes || '',
    statut: police?.statut || 'en_cours',
    entite_id: police?.entite_id || '',
    police_liee_id: police?.police_liee_id || ''
  })
  const [entites, setEntites] = useState([])
  const [policesRC, setPolicesRC] = useState([])
  const isTransport = ['rc_transport', 'ad_valorem'].includes(formData.type_police)

  useEffect(() => {
    if (!isTransport || !formData.tenant_id) { setEntites([]); setPolicesRC([]); return }
    fetch(`${API_URL}/api/entites?tenant_id=${formData.tenant_id}&actif=true`).then(r => r.json()).then(d => setEntites(d.entites || [])).catch(() => {})
    fetch(`${API_URL}/api/polices?tenant_id=${formData.tenant_id}`).then(r => r.json()).then(d => setPolicesRC((d.polices || []).filter(p => p.type_police === 'rc_transport'))).catch(() => {})
  }, [isTransport, formData.tenant_id])

  const lockedTenantName = tenants.find(t => t.id === lockedTenantId)?.nom

  const handleSubmit = (e) => {
    e.preventDefault()
    const data = { ...formData }
    data.franchise_standard = data.franchise_standard !== '' && data.franchise_standard != null ? parseFloat(data.franchise_standard) : null
    data.plafond_garantie = data.plafond_garantie !== '' && data.plafond_garantie != null ? parseFloat(data.plafond_garantie) : null
    if (!data.compagnie_id) data.compagnie_id = null
    if (!isTransport) { delete data.entite_id; delete data.police_liee_id }
    else { data.entite_id = data.entite_id || null; data.police_liee_id = data.police_liee_id || null }
    onSave(data)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-xl shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-800">
            {police ? 'Modifier la police' : 'Nouvelle police'}
          </h3>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Client *</label>
              {isClientLocked ? (
                <div className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 text-slate-700 font-medium" data-testid="police-client-locked">
                  {lockedTenantName || lockedTenantId}
                </div>
              ) : (
                <select
                  value={formData.tenant_id}
                  onChange={e => setFormData({...formData, tenant_id: e.target.value})}
                  required
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  data-testid="police-client-select"
                >
                  <option value="">Sélectionner...</option>
                  {tenants.map(t => (
                    <option key={t.id} value={t.id}>{t.nom}</option>
                  ))}
                </select>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Compagnie *</label>
              <select
                value={formData.compagnie_id}
                onChange={e => setFormData({...formData, compagnie_id: e.target.value})}
                required
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              >
                <option value="">Sélectionner...</option>
                {compagnies.map(c => (
                  <option key={c.id} value={c.id}>{c.nom}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">N° Police *</label>
              <input
                type="text"
                value={formData.numero_police}
                onChange={e => setFormData({...formData, numero_police: e.target.value})}
                required
                disabled={!!police}
                placeholder="AXA-RC-2024-001"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Type *</label>
              <select
                value={formData.type_police}
                onChange={e => setFormData({...formData, type_police: e.target.value})}
                required
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              >
                {Object.entries(TYPE_POLICE_CONFIG).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
            </div>
          </div>

          {isTransport && (
            <div className="grid grid-cols-2 gap-4 bg-sky-50/60 border border-sky-100 rounded-lg p-3" data-testid="police-transport-fields">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Entité couverte</label>
                <select value={formData.entite_id} onChange={e => setFormData({...formData, entite_id: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white" data-testid="police-entite-select">
                  <option value="">— Groupe (toutes entités) —</option>
                  {entites.map(e => <option key={e.id} value={e.id}>{e.chemin}</option>)}
                </select>
                <p className="text-[10px] text-slate-400 mt-0.5">La police d'une entité mère couvre ses filiales.</p>
              </div>
              {formData.type_police === 'ad_valorem' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Police RC transport liée</label>
                  <select value={formData.police_liee_id} onChange={e => setFormData({...formData, police_liee_id: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white" data-testid="police-liee-select">
                    <option value="">— aucune —</option>
                    {policesRC.map(p => <option key={p.id} value={p.id}>{`${p.numero_police} — ${p.libelle || ''}`}</option>)}
                  </select>
                </div>
              )}
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Libellé</label>
            <input
              type="text"
              value={formData.libelle}
              onChange={e => setFormData({...formData, libelle: e.target.value})}
              placeholder="Police RC Professionnelle 2024"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Date d'effet *</label>
              <input
                type="date"
                value={formData.date_effet}
                onChange={e => setFormData({...formData, date_effet: e.target.value})}
                required
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Date d'échéance *</label>
              <input
                type="date"
                value={formData.date_echeance}
                onChange={e => setFormData({...formData, date_echeance: e.target.value})}
                required
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Franchise (€)</label>
              <input
                type="number"
                value={formData.franchise_standard}
                onChange={e => setFormData({...formData, franchise_standard: e.target.value})}
                placeholder="500"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Plafond garantie (€)</label>
              <input
                type="number"
                value={formData.plafond_garantie}
                onChange={e => setFormData({...formData, plafond_garantie: e.target.value})}
                placeholder="1000000"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              />
            </div>
          </div>
          
          {police && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Statut</label>
              <select
                value={formData.statut}
                onChange={e => setFormData({...formData, statut: e.target.value})}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              >
                {Object.entries(STATUT_CONFIG).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={e => setFormData({...formData, notes: e.target.value})}
              rows={2}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none"
            />
          </div>
          
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {police ? 'Mettre à jour' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}


// ── Prime History Panel ─────────────────────────────────────────────────
function PrimeHistoryPanel({ police, onClose }) {
  const [primes, setPrimes] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState(null)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({ annee: new Date().getFullYear(), prime_ht: '', prime_ttc: '', taxes: '' })

  const fetchPrimes = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/prime-history?police_id=${police.id}`)
      const d = await r.json()
      setPrimes(d.primes || [])
    } catch (e) { console.error(e) }
    finally { setLoading(false) }
  }, [police.id])

  useEffect(() => { fetchPrimes() }, [fetchPrimes])

  const handleSave = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      const payload = {
        tenant_id: police.tenant_id,
        police_id: police.id,
        annee: parseInt(form.annee),
        prime_ht: parseFloat(form.prime_ht) || null,
        prime_ttc: parseFloat(form.prime_ttc) || null,
        taxes: parseFloat(form.taxes) || 0,
      }
      const url = editing
        ? `${API_URL}/api/prime-history/${editing.id}`
        : `${API_URL}/api/prime-history`
      const method = editing ? 'PUT' : 'POST'
      const r = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editing ? { prime_ht: payload.prime_ht, prime_ttc: payload.prime_ttc, taxes: payload.taxes, annee: payload.annee } : payload),
      })
      if (!r.ok) {
        const d = await r.json().catch(() => ({}))
        throw new Error(d.detail || 'Erreur')
      }
      toast.success(editing ? 'Prime mise à jour' : 'Prime ajoutée')
      setShowForm(false)
      setEditing(null)
      setForm({ annee: new Date().getFullYear(), prime_ht: '', prime_ttc: '', taxes: '' })
      fetchPrimes()
    } catch (e) { toast.error(e.message) }
    finally { setSaving(false) }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette prime ?')) return
    try {
      const r = await fetch(`${API_URL}/api/prime-history/${id}`, { method: 'DELETE' })
      if (r.ok) { toast.success('Prime supprimée'); fetchPrimes() }
    } catch (e) { toast.error(e.message) }
  }

  const startEdit = (p) => {
    setEditing(p)
    setForm({
      annee: p.annee,
      prime_ht: p.prime_nette || '',
      prime_ttc: p.prime_totale || '',
      taxes: p.taxes || '',
    })
    setShowForm(true)
  }

  const SOURCE_STYLES = {
    prime_history_ht: { label: 'HT confirmé', bg: 'bg-emerald-50 text-emerald-700' },
    migration_v1: { label: 'Migration V1', bg: 'bg-amber-50 text-amber-700' },
    seed_initial: { label: 'Seed initial', bg: 'bg-slate-100 text-slate-500' },
    saisie_manuelle: { label: 'Saisie manuelle', bg: 'bg-blue-50 text-blue-700' },
    import: { label: 'Import', bg: 'bg-purple-50 text-purple-700' },
  }

  const fmtM = (v) => v ? `${Number(v).toLocaleString('fr-FR')} €` : '—'

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50" data-testid="prime-history-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col" data-testid="prime-history-panel">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
              <Euro size={16} className="text-emerald-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800 text-sm" data-testid="prime-history-title">
                Historique primes — {police.numero_police}
              </h3>
              <p className="text-[10px] text-slate-400">{police.libelle}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setEditing(null); setForm({ annee: new Date().getFullYear(), prime_ht: '', prime_ttc: '', taxes: '' }); setShowForm(!showForm) }}
              className="flex items-center gap-1 text-xs px-3 py-1.5 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors"
              data-testid="prime-add-btn"
            >
              <Plus size={12} /> Ajouter
            </button>
            <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg">
              <XCircle size={16} className="text-slate-400" />
            </button>
          </div>
        </div>

        {/* Inline Form */}
        {showForm && (
          <form onSubmit={handleSave} className="px-6 py-3 bg-slate-50/80 border-b border-slate-100" data-testid="prime-form">
            <div className="grid grid-cols-4 gap-3">
              <div>
                <label className="block text-[10px] font-medium text-slate-500 mb-1">Exercice *</label>
                <input
                  type="number"
                  min="2000"
                  max="2099"
                  value={form.annee}
                  onChange={e => setForm(p => ({ ...p, annee: e.target.value }))}
                  className="w-full text-sm border border-slate-200 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-slate-200"
                  required
                  data-testid="prime-form-annee"
                />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-slate-500 mb-1">Prime HT * <span className="text-emerald-600">(S/P)</span></label>
                <input
                  type="number"
                  step="0.01"
                  value={form.prime_ht}
                  onChange={e => setForm(p => ({ ...p, prime_ht: e.target.value }))}
                  placeholder="ex: 45000"
                  className="w-full text-sm border border-slate-200 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-slate-200"
                  required
                  data-testid="prime-form-ht"
                />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-slate-500 mb-1">Prime TTC</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.prime_ttc}
                  onChange={e => setForm(p => ({ ...p, prime_ttc: e.target.value }))}
                  placeholder="optionnel"
                  className="w-full text-sm border border-slate-200 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-slate-200"
                  data-testid="prime-form-ttc"
                />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-slate-500 mb-1">Taxes</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.taxes}
                  onChange={e => setForm(p => ({ ...p, taxes: e.target.value }))}
                  placeholder="optionnel"
                  className="w-full text-sm border border-slate-200 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-slate-200"
                  data-testid="prime-form-taxes"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 mt-3">
              <button
                type="button"
                onClick={() => { setShowForm(false); setEditing(null) }}
                className="text-xs text-slate-500 px-3 py-1.5 border border-slate-200 rounded-lg hover:bg-white"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-1 text-xs text-white bg-emerald-600 px-3 py-1.5 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                data-testid="prime-form-submit"
              >
                {saving ? <Loader2 size={12} className="animate-spin" /> : null}
                {editing ? 'Mettre à jour' : 'Enregistrer'}
              </button>
            </div>
          </form>
        )}

        {/* Table */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-slate-300" />
            </div>
          ) : primes.length === 0 ? (
            <div className="text-center py-8 text-sm text-slate-400" data-testid="prime-history-empty">
              Aucune prime renseignée pour cette police.
            </div>
          ) : (
            <table className="w-full text-xs" data-testid="prime-history-table">
              <thead>
                <tr className="bg-slate-50 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                  <th className="px-3 py-2.5 text-left">Exercice</th>
                  <th className="px-3 py-2.5 text-right">Prime HT</th>
                  <th className="px-3 py-2.5 text-right">Taxes</th>
                  <th className="px-3 py-2.5 text-right">Prime TTC</th>
                  <th className="px-3 py-2.5 text-left">Source</th>
                  <th className="px-3 py-2.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {primes.map(p => {
                  const src = SOURCE_STYLES[p.source] || SOURCE_STYLES.saisie_manuelle
                  return (
                    <tr key={p.id} className="hover:bg-slate-50/50" data-testid={`prime-row-${p.annee}`}>
                      <td className="px-3 py-2.5 font-semibold text-slate-800">{p.annee}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-emerald-700 font-medium">{fmtM(p.prime_nette)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-slate-500">{fmtM(p.taxes)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-slate-700">{fmtM(p.prime_totale)}</td>
                      <td className="px-3 py-2.5">
                        <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${src.bg}`} data-testid="prime-source-badge">{src.label}</span>
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => startEdit(p)}
                            className="p-1 text-slate-400 hover:text-blue-600 rounded"
                            title="Modifier"
                            data-testid={`prime-edit-${p.annee}`}
                          >
                            <Edit size={12} />
                          </button>
                          <button
                            onClick={() => handleDelete(p.id)}
                            className="p-1 text-slate-400 hover:text-red-600 rounded"
                            title="Supprimer"
                            data-testid={`prime-delete-${p.annee}`}
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}

          {/* Info box */}
          <div className="mt-4 p-3 bg-slate-50 rounded-lg border border-slate-100">
            <p className="text-[10px] text-slate-500 leading-relaxed">
              <span className="font-semibold text-slate-700">Règle S/P :</span> Le ratio sinistres/primes est calculé sur la <span className="font-semibold text-emerald-700">Prime HT</span>.
              La Prime TTC est utilisée pour la vision budgétaire DAF.
              Les primes issues de la migration V1 ont une distinction HT/TTC non confirmée.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
